import {
  readAccessToken,
  readRefreshToken,
  writeAccessToken,
  writeRefreshToken,
  logout as authLogout,
} from "../functions/authFunctions";

// 소셜 로그인 공통 유틸리티 함수들

export const SocialAuth = {
  // 카카오 로그인 페이지로 리다이렉트
  kakao: {
    login: () => {
      const kakaoAuthUrl = `${process.env.REACT_APP_API_HOST}/auth/kakao`;
      window.location.href = kakaoAuthUrl;
    },
  },

  // 네이버 로그인 페이지로 리다이렉트
  naver: {
    login: () => {
      const naverAuthUrl = `${process.env.REACT_APP_API_HOST}/auth/naver`;
      window.location.href = naverAuthUrl;
    },
  },

  guest: {
    login: (mutateFn: () => void) => {
      mutateFn(); // 컴포넌트에서 전달된 mutate 호출
    },
  },

  admin: {
    login: (mutateFn: () => void) => {
      mutateFn(); // 컴포넌트에서 전달된 mutate 호출
    },
  },

  // 구글 로그인 페이지로 리다이렉트
  google: {
    login: () => {
      const googleAuthUrl = `${process.env.REACT_APP_API_HOST}/auth/google`;
      window.location.href = googleAuthUrl;
    },
  },

  // 애플 로그인 페이지로 리다이렉트
  apple: {
    login: () => {
      const appleAuthUrl = `${process.env.REACT_APP_API_HOST}/auth/apple`;
      window.location.href = appleAuthUrl;
    },
  },

  // 공통 로그아웃
  logout: async () => {
    try {
      const accessToken = readAccessToken();

      if (accessToken) {
        // 백엔드에 로그아웃 요청
        await fetch(`${process.env.REACT_APP_API_HOST}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });
      }
    } catch (error) {
      console.error("로그아웃 오류:", error);
    } finally {
      // 기존 함수 사용해서 토큰 제거 및 홈으로 이동
      authLogout();
    }
  },

  // 토큰 유효성 검사
  isAuthenticated: () => {
    const accessToken = readAccessToken();
    return !!accessToken;
  },

  // 토큰 가져오기
  getAccessToken: () => {
    return readAccessToken();
  },

  // 리프레시 토큰으로 새 액세스 토큰 발급
  refreshToken: async () => {
    try {
      const refreshToken = readRefreshToken();

      if (!refreshToken) {
        throw new Error("리프레시 토큰이 없습니다.");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/auth/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken }),
        }
      );

      if (!response.ok) {
        throw new Error("토큰 갱신에 실패했습니다.");
      }

      const data = await response.json();

      if (data.accessToken) {
        writeAccessToken(data.accessToken);
        if (data.refreshToken) {
          writeRefreshToken(data.refreshToken);
        }
        return data.accessToken;
      }

      throw new Error("새 토큰을 받을 수 없습니다.");
    } catch (error) {
      console.error("토큰 갱신 오류:", error);
      // 토큰 갱신 실패 시 로그아웃 처리
      SocialAuth.logout();
      throw error;
    }
  },
};

// 기존 호환성을 위한 KakaoAuth export
export const KakaoAuth = {
  login: SocialAuth.kakao.login,
  logout: SocialAuth.logout,
  isAuthenticated: SocialAuth.isAuthenticated,
  getAccessToken: SocialAuth.getAccessToken,
  refreshToken: SocialAuth.refreshToken,
};

// 네이버 로그인 전용 export
export const NaverAuth = {
  login: SocialAuth.naver.login,
  logout: SocialAuth.logout,
  isAuthenticated: SocialAuth.isAuthenticated,
  getAccessToken: SocialAuth.getAccessToken,
  refreshToken: SocialAuth.refreshToken,
};

// 애플 로그인 전용 export
export const AppleAuth = {
  login: SocialAuth.apple.login,
  logout: SocialAuth.logout,
  isAuthenticated: SocialAuth.isAuthenticated,
  getAccessToken: SocialAuth.getAccessToken,
  refreshToken: SocialAuth.refreshToken,
};

// 게스트 로그인 전용 export
export const GuestAuth = {
  login: SocialAuth.guest.login,
  logout: SocialAuth.logout,
  isAuthenticated: SocialAuth.isAuthenticated,
  getAccessToken: SocialAuth.getAccessToken,
  refreshToken: SocialAuth.refreshToken,
};

// 어드민 로그인 전용 export
export const AdminAuth = {
  login: SocialAuth.admin.login,
  logout: SocialAuth.logout,
  isAuthenticated: SocialAuth.isAuthenticated,
  getAccessToken: SocialAuth.getAccessToken,
  refreshToken: SocialAuth.refreshToken,
};
